import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { relatorioEstagioService, agenteIntegradorService } from '@/services/api';
import { AgenteIntegrador, PaginationParams, RelatorioEstagio, PaginationResponse, RelatorioEstagioExportResponse } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, DocumentTextIcon, MagnifyingGlassIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';
import * as XLSX from 'xlsx';

export const RelatoriosPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();
  const { handleError } = useErrorHandler();
  const [filters, setFilters] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    tipo: undefined,
    status: undefined,
    agenteIntegradorId: undefined,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [permissoesExportacao, setPermissoesExportacao] = useState<RelatorioEstagioExportResponse['permissoes'] | null>(null);

  // Debounce do campo de busca - evita API call a cada tecla
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchTerm.trim() || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Buscar relatórios - placeholderData mantém dados anteriores visíveis durante refetch (só atualiza a tabela)
  const { data: relatoriosData, isLoading, isFetching, error, refetch } = useQuery<PaginationResponse<RelatorioEstagio>>({
    queryKey: ['relatorios', filters],
    queryFn: () => relatorioEstagioService.list(filters),
    staleTime: 30000, // 30 segundos
    placeholderData: (previousData) => previousData,
  });

  type HandleErrorArg = Parameters<typeof handleError>[0];

  useEffect(() => {
    if (error) {
      const message = handleError(error as HandleErrorArg, 'Erro ao carregar relatórios');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (relatoriosData) {
      setLoadError(null);
    }
  }, [relatoriosData]);

  // Mutation para deletar relatório
  const deleteRelatorioMutation = useMutation({
    mutationFn: relatorioEstagioService.delete,
    onSuccess: (_data, deletedId) => {
      toast.success('Relatório excluído com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['relatorios'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      if (deletedId !== undefined && deletedId !== null) {
        queryClient.invalidateQueries({ queryKey: ['relatorio', deletedId] });
      }
    },
    onError: (error: HandleErrorArg) => {
      const errorMessage = error.response?.data?.error || 'Erro ao excluir relatório';
      toast.error(errorMessage);
    },
  });

  // Handlers
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number, tipo: string) => {
    showConfirm(
      `Excluir ${tipo}`,
      'Tem certeza que deseja excluir este relatório? Esta ação não pode ser desfeita.',
      async () => {
        await deleteRelatorioMutation.mutateAsync(id);
      },
      'danger'
    );
  };

  const handleFilterChange = <K extends keyof PaginationParams>(key: K, value: PaginationParams[K]) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ page: 1, limit: 10, search: '', tipo: undefined, status: undefined, agenteIntegradorId: undefined });
  };

  // Funções auxiliares
  const getEstagioInfo = (relatorio: RelatorioEstagio) => {
    if (relatorio.estagio) {
      return {
        aluno: relatorio.estagio.aluno?.nome || 'Aluno não encontrado',
        empresa: relatorio.estagio.empresa?.nome || 'Empresa não encontrada',
        agenteIntegrador: relatorio.estagio.agenteIntegrador?.nome || 'Sem agente',
      };
    }

    return {
      aluno: 'Aluno não informado',
      empresa: 'Empresa não informada',
      agenteIntegrador: 'Sem agente',
    };
  };

  const getTipoColor = (tipo: string) => {
    const colors = {
      'Inicial': 'bg-blue-100 text-blue-800 border-blue-200',
      'Parcial': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Final': 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[tipo as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusInfo = (dataEntregue: string | null | undefined, prazoEntrega: string) => {
    if (!dataEntregue) {
      const prazo = new Date(prazoEntrega);
      const hoje = new Date();
      if (hoje > prazo) {
        return { text: 'Atrasado', color: 'bg-red-100 text-red-800 border-red-200' };
      }
      return { text: 'Pendente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    }
    return { text: 'Entregue', color: 'bg-green-100 text-green-800 border-green-200' };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const relatorios = relatoriosData?.data || [];
  const pagination = relatoriosData?.pagination;

  const exportarExcel = async () => {
    const totalRelatorios = pagination?.total ?? 0;

    if (totalRelatorios === 0) {
      toast.error('Não há relatórios para exportar');
      return;
    }

    try {
      setExportando(true);

      const exportFilters: PaginationParams = {
        ...filters,
        page: 1,
        // O backend limita em 100, então usamos o máximo permitido
        limit: 100,
      };

      const exportResponse = await relatorioEstagioService.exportarLista(exportFilters);

      setPermissoesExportacao(exportResponse.permissoes);

      if (!exportResponse.permissoes.podeExportar) {
        const mensagemPlano = exportResponse.permissoes.mensagem || 'Seu plano não permite mais exportações de relatórios neste período.';
        toast.error(mensagemPlano);
        return;
      }

      const dadosExportar = exportResponse.dados ?? [];

      if (!dadosExportar.length) {
        toast.error('Não há relatórios para exportar');
        return;
      }

      const dadosParaExportar = dadosExportar.map((relatorio) => {
        const estagioInfo = getEstagioInfo(relatorio);
        const statusInfo = getStatusInfo(relatorio.dataEntregue, relatorio.prazoEntrega);

        return {
          'ID Relatório': relatorio.id,
          'Tipo': relatorio.tipo,
          'Status': statusInfo.text,
          'Estágio ID': relatorio.estagioId,
          'Aluno': estagioInfo.aluno,
          'Empresa': estagioInfo.empresa,
          'Agente Integrador': estagioInfo.agenteIntegrador,
          'Prazo de Entrega': formatDate(relatorio.prazoEntrega),
          'Data Entregue': relatorio.dataEntregue ? formatDate(relatorio.dataEntregue) : '',
          'Observações': relatorio.observacao || '',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatorios');

      const maxWidth = 30;
      const colWidths = Object.keys(dadosParaExportar[0] || {}).map(() => ({ wch: maxWidth }));
      worksheet['!cols'] = colWidths;

      const fileName = `relatorios-estagio-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success('Relatórios de estágio exportados com sucesso!');
    } catch (err) {
      const error = err as { response?: { data?: { error?: string } } };
      const mensagem = error?.response?.data?.error || 'Erro ao exportar relatórios de estágio';
      console.error('Erro ao exportar relatórios de estágio:', err);
      toast.error(mensagem);
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatórios de Estágio</h1>
          <p className="mt-1 text-gray-600">
            Gerencie os relatórios dos estágios cadastrados
          </p>
          {permissoesExportacao && permissoesExportacao.limiteMensal !== null && (
            <p className="mt-1 text-xs text-gray-500">
              Exportações restantes no mês: {permissoesExportacao.limiteRestante ?? 0} de {permissoesExportacao.limiteMensal}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto flex items-center justify-center bg-green-600 hover:bg-green-700 text-white"
            onClick={exportarExcel}
            disabled={
              isLoading ||
              exportando ||
              !pagination ||
              (pagination.total ?? 0) === 0
            }
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {exportando ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          <Link to="/relatorios/novo">
            <Button asChild className="bg-primary-600 hover:bg-primary-700 w-full sm:w-auto">
              <div className="flex items-center justify-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo Relatório
              </div>
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{pagination?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Entregues</p>
                <p className="text-2xl font-bold text-gray-900">
                  {relatorios.filter(r => r.dataEntregue).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {relatorios.filter(r => !r.dataEntregue).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Páginas</p>
                <p className="text-2xl font-bold text-gray-900">{pagination?.totalPages || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>Filtros</span>
            <span className="text-sm font-normal text-gray-500">
              ({pagination?.total || 0} relatórios encontrados)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Aluno, empresa, observação..."
                  className="w-full pl-10 pr-8"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                value={filters.tipo || ''}
                onChange={(e) => handleFilterChange('tipo', (e.target.value || undefined) as PaginationParams['tipo'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os tipos</option>
                <option value="Inicial">Inicial</option>
                <option value="Parcial">Parcial</option>
                <option value="Final">Final</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', (e.target.value || undefined) as PaginationParams['status'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Todos os status</option>
                <option value="pendente">Pendente</option>
                <option value="entregue">Entregue</option>
                <option value="atrasado">Atrasado</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Agente Integrador
              </label>
              <ServerSearchSelect<AgenteIntegrador>
                value={filters.agenteIntegradorId ?? null}
                onChange={(selectedId) => handleFilterChange('agenteIntegradorId', selectedId ?? undefined)}
                fetchPage={(params) => agenteIntegradorService.list(params)}
                fetchById={(selectedId) => agenteIntegradorService.getById(selectedId)}
                getOptionLabel={(agente) => agente.nome}
                queryKey="agentes-integradores-relatorios-select"
                placeholder="Todos os agentes"
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="w-full"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Relatórios */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Relatórios</CardTitle>
          <CardDescription>
            {relatorios.length === 0 && !isFetching
              ? 'Nenhum relatório encontrado'
              : `${relatorios.length} relatório(s) encontrado(s)`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="relative">
          {loadError ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-6xl mb-4">⚠️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar relatórios</h3>
              <p className="text-gray-500 mb-6">{loadError}</p>
              <Button onClick={() => refetch()} className="bg-primary-600 hover:bg-primary-700">
                Tentar novamente
              </Button>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-3 text-gray-600">Carregando relatórios...</p>
              </div>
            </div>
          ) : relatorios.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum relatório encontrado
              </h3>
              <p className="text-gray-500 mb-6">
                {filters.search || filters.tipo || filters.status || filters.agenteIntegradorId
                  ? 'Tente ajustar os filtros para encontrar relatórios.'
                  : 'Comece criando um novo relatório de estágio.'
                }
              </p>
              <Link to="/relatorios/novo">
                <Button asChild>
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Novo Relatório
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {isFetching && (
                <div className="absolute top-4 right-4 flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                  Atualizando...
                </div>
              )}
              <div className="space-y-4">
              {relatorios.map((relatorio) => {
                const estagioInfo = getEstagioInfo(relatorio);
                const statusInfo = getStatusInfo(relatorio.dataEntregue, relatorio.prazoEntrega);

                return (
                  <div
                    key={relatorio.id}
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 bg-white"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      {/* Informações principais */}
                      <div className="flex-1 space-y-4">
                        {/* Header do relatório */}
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                            <h3 className="text-lg font-semibold text-gray-900">
                              Relatório #{relatorio.id}
                            </h3>
                          </div>

                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getTipoColor(relatorio.tipo)}`}>
                            {relatorio.tipo}
                          </span>

                          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>

                        {/* Informações do estágio */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Estágio</p>
                            <p className="font-medium text-gray-900">#{relatorio.estagioId}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Prazo de Entrega</p>
                            <p className="font-medium text-gray-900">
                              {formatDate(relatorio.prazoEntrega)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Data Entregue</p>
                            <p className="font-medium text-gray-900">
                              {relatorio.dataEntregue
                                ? formatDate(relatorio.dataEntregue)
                                : 'Não entregue'
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Observações</p>
                            <p className="font-medium text-gray-900 truncate">
                              {relatorio.observacao || 'Nenhuma'}
                            </p>
                          </div>
                        </div>

                        {/* Informações do aluno e empresa */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Aluno</p>
                            <p className="font-medium text-gray-900">{estagioInfo.aluno}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Empresa</p>
                            <p className="font-medium text-gray-900">{estagioInfo.empresa}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Agente Integrador</p>
                            <p className="font-medium text-gray-900">{estagioInfo.agenteIntegrador}</p>
                          </div>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="flex items-center gap-2 whitespace-nowrap min-w-[7rem] border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 hover:border-yellow-300 hover:text-blue-800 shadow-sm"
                          title="Editar"
                        >
                          <Link to={`/relatorios/${relatorio.id}/editar`} className="flex items-center gap-2 w-full justify-center">
                            <PencilIcon className="h-4 w-4 flex-shrink-0" />
                            Editar
                          </Link>
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(relatorio.id, relatorio.tipo)}
                          disabled={deleteRelatorioMutation.isPending}
                          className="flex items-center gap-2 border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:border-red-300 hover:text-red-800 shadow-sm"
                          title="Excluir"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>

                  {/* Paginação */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex justify-center mt-8">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange((filters.page || 1) - 1)}
                      disabled={(filters.page || 1) <= 1}
                    >
                      Anterior
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={(filters.page || 1) === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-10"
                          >
                            {page}
                          </Button>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange((filters.page || 1) + 1)}
                      disabled={(filters.page || 1) >= pagination.totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteRelatorioMutation.isPending}
      />
    </div>
  );
};
